import { useEffect, useState, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { teamStore } from '@/stores/TeamStore'
import { teamExecutionStore } from '@/stores/TeamExecutionStore'
import { agentStore } from '@/stores/AgentStore'
import { hpcStore } from '@/stores/HPCStore'
import { useTeamExecutionUpdates, TeamExecutionUpdate } from '@/hooks/useWebSocket'
import {
  ArrowLeftIcon,
  PlayIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  ComputerDesktopIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline'
import { TeamStatus, AgentRole, ExecutionMode, HPCResourceConfig, HPCTrainingConfig } from '@/types'

const TeamExecutionPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [inputMode, setInputMode] = useState<'form' | 'json'>('form')
  const [taskInput, setTaskInput] = useState('')
  const [jsonInput, setJsonInput] = useState('')
  const [currentExecutionId, setCurrentExecutionId] = useState<string | undefined>(undefined)
  const [latestUpdate, setLatestUpdate] = useState<TeamExecutionUpdate | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // HPC execution state
  const [executionMode, setExecutionMode] = useState<ExecutionMode>('local')
  const [selectedCluster, setSelectedCluster] = useState<string>('')
  const [hpcResources, setHpcResources] = useState<Partial<HPCResourceConfig>>({
    nodes: 2,
    cpus_per_node: 8,
    gpus: 2,
    memory_gb: 32,
    walltime_hours: 8,
  })
  const [hpcTraining, setHpcTraining] = useState<Partial<HPCTrainingConfig>>({
    job_type: 'multi_agent_simulation',
    checkpoint_enabled: true,
    checkpoint_interval_minutes: 30,
    auto_resume: true,
  })

  // Real-time WebSocket updates for team execution
  useTeamExecutionUpdates(currentExecutionId, (update) => {
    setLatestUpdate(update)

    // Handle different update types
    if (update.type === 'team_execution.start') {
      toast.success(`Team execution started: ${update.team_name}`)
    } else if (update.type === 'team_execution.agent_start') {
      toast(`Agent starting: ${update.agent_name}`, { icon: '🤖' })
    } else if (update.type === 'team_execution.agent_complete') {
      toast.success(`Agent completed: ${update.agent_name}`)
    } else if (update.type === 'team_execution.complete') {
      toast.success(`Team execution completed in ${update.duration_ms}ms!`)
      // Refresh execution data to get final results
      if (currentExecutionId) {
        teamExecutionStore.fetchExecutionById(currentExecutionId)
        teamExecutionStore.fetchMessages(currentExecutionId)
      }
    } else if (update.type === 'team_execution.error') {
      toast.error(`Execution failed: ${update.error}`)
    }
  })

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [teamExecutionStore.messages.length])

  useEffect(() => {
    const loadTeam = async () => {
      if (!id) {
        navigate('/teams')
        return
      }

      setIsLoading(true)
      try {
        await teamStore.fetchTeamById(id)
        await agentStore.fetchAgents()
        // Fetch HPC clusters for the dropdown
        hpcStore.fetchClusters()

        const team = teamStore.selectedTeam
        if (!team) {
          toast.error('Team not found')
          navigate('/teams')
          return
        }

        if (team.status !== TeamStatus.ACTIVE) {
          toast.error('Only active teams can be executed')
          navigate('/teams')
          return
        }

        // Initialize inputs
        setTaskInput('')
        setJsonInput(JSON.stringify({ task: '', requirements: [] }, null, 2))
      } catch (error) {
        toast.error('Failed to load team')
        navigate('/teams')
      } finally {
        setIsLoading(false)
      }
    }

    loadTeam()
  }, [id, navigate])

  const handleExecute = async () => {
    if (!id) return

    // Validate HPC mode
    if (executionMode === 'hpc' && !selectedCluster) {
      toast.error('Please select an HPC cluster')
      return
    }

    try {
      let inputData: Record<string, any>

      if (inputMode === 'json') {
        try {
          inputData = JSON.parse(jsonInput)
        } catch (e) {
          toast.error('Invalid JSON input')
          return
        }
      } else {
        if (!taskInput.trim()) {
          toast.error('Task description is required')
          return
        }
        inputData = { task: taskInput }
      }

      // Add HPC configuration to input if in HPC mode
      if (executionMode === 'hpc') {
        inputData._execution_mode = 'hpc'
        inputData._hpc_resources = {
          cluster_id: selectedCluster,
          ...hpcResources,
        }
        inputData._hpc_training = hpcTraining
      }

      const execution = await teamExecutionStore.executeTeam(id, inputData)

      if (execution) {
        // Set execution ID to enable WebSocket real-time updates
        setCurrentExecutionId(execution.id)
        if (executionMode === 'hpc') {
          toast.success('Team HPC job submitted! Check HPC Computing page for status.')
        } else {
          toast.success('Team execution started! Real-time updates enabled.')
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to start team execution')
    }
  }

  const handleCancel = async () => {
    const execution = teamExecutionStore.selectedExecution
    if (!execution) return

    if (window.confirm('Are you sure you want to cancel this execution?')) {
      try {
        await teamExecutionStore.cancelExecution(execution.id)
        toast.success('Execution cancelled')
      } catch (error) {
        toast.error('Failed to cancel execution')
      }
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />
      case 'FAILED':
        return <XCircleIcon className="w-5 h-5 text-red-600" />
      case 'RUNNING':
        return <ClockIcon className="w-5 h-5 text-blue-600 animate-spin" />
      case 'CANCELLED':
        return <XCircleIcon className="w-5 h-5 text-muted-foreground" />
      default:
        return <ClockIcon className="w-5 h-5 text-muted-foreground" />
    }
  }

  const getStatusBadgeVariant = (status: string) => {
    const variants: Record<string, 'success' | 'error' | 'warning' | 'gray'> = {
      COMPLETED: 'success',
      FAILED: 'error',
      RUNNING: 'warning',
      PENDING: 'gray',
      CANCELLED: 'gray',
    }
    return variants[status] || 'gray'
  }

  const getRoleBadgeVariant = (role: AgentRole) => {
    const variants: Record<AgentRole, 'primary' | 'success' | 'warning' | 'error' | 'gray'> = {
      [AgentRole.COORDINATOR]: 'primary',
      [AgentRole.RESEARCHER]: 'success',
      [AgentRole.ANALYST]: 'warning',
      [AgentRole.WRITER]: 'primary',
      [AgentRole.REVIEWER]: 'warning',
      [AgentRole.VALIDATOR]: 'error',
      [AgentRole.TESTER]: 'success',
      [AgentRole.DEBUGGER]: 'error',
      [AgentRole.OPTIMIZER]: 'success',
      [AgentRole.SPECIALIST]: 'primary',
      [AgentRole.CUSTOM]: 'gray',
    }
    return variants[role]
  }

  const getAgentName = (agentId: string) => {
    const agent = agentStore.agents.find((a) => a.id === agentId)
    return agent?.name || agentId
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading team...</p>
        </div>
      </div>
    )
  }

  const team = teamStore.selectedTeam
  if (!team) return null

  const execution = teamExecutionStore.selectedExecution
  const messages = teamExecutionStore.messages
  const isExecuting = execution?.status === 'RUNNING' || execution?.status === 'PENDING'

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/teams/${id}`)}>
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Back to Team
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Execute Team: {team.name}</h1>
            <p className="text-muted-foreground mt-1">{team.description || 'No description'}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Input Section */}
          {!execution && (
            <Card>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-foreground">Execution Input</h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setInputMode('form')}
                      className={`px-3 py-1 text-sm rounded-lg ${
                        inputMode === 'form'
                          ? 'bg-primary-500/15 text-primary-700 font-medium'
                          : 'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      Form
                    </button>
                    <button
                      onClick={() => setInputMode('json')}
                      className={`px-3 py-1 text-sm rounded-lg ${
                        inputMode === 'json'
                          ? 'bg-primary-500/15 text-primary-700 font-medium'
                          : 'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      JSON
                    </button>
                  </div>
                </div>

                {inputMode === 'form' ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Task Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={taskInput}
                        onChange={(e) => setTaskInput(e.target.value)}
                        placeholder="Describe the task for this team to complete..."
                        rows={6}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Provide a clear description of what you want the team to accomplish
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      JSON Input
                    </label>
                    <textarea
                      value={jsonInput}
                      onChange={(e) => setJsonInput(e.target.value)}
                      placeholder='{"task": "Your task here...", "requirements": []}'
                      rows={10}
                      className="w-full px-3 py-2 border border-border rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Provide input as JSON object. Must include "task" field.
                    </p>
                  </div>
                )}

                {/* Execution Mode Toggle */}
                <div className="border-t border-border pt-4 mt-4">
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Execution Mode
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setExecutionMode('local')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                        executionMode === 'local'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-border hover:border-border text-muted-foreground'
                      }`}
                    >
                      <CpuChipIcon className="w-5 h-5" />
                      <span className="font-medium">Local</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setExecutionMode('hpc')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                        executionMode === 'hpc'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-border hover:border-border text-muted-foreground'
                      }`}
                    >
                      <ComputerDesktopIcon className="w-5 h-5" />
                      <span className="font-medium">HPC Cluster</span>
                    </button>
                  </div>
                </div>

                {/* HPC Configuration */}
                {executionMode === 'hpc' && (
                  <div className="border border-border rounded-lg p-4 space-y-4 bg-muted/50">
                    <h4 className="font-medium text-foreground flex items-center gap-2">
                      <ComputerDesktopIcon className="w-5 h-5" />
                      HPC Configuration
                    </h4>

                    {/* Cluster Selection */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        HPC Cluster *
                      </label>
                      <select
                        value={selectedCluster}
                        onChange={(e) => setSelectedCluster(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Select a cluster...</option>
                        {hpcStore.clusters.map((cluster) => (
                          <option key={cluster.id} value={cluster.id}>
                            {cluster.name} ({cluster.hostname})
                          </option>
                        ))}
                      </select>
                      {hpcStore.clusters.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          No clusters configured. <a href="/hpc" className="underline">Add a cluster</a> first.
                        </p>
                      )}
                    </div>

                    {/* Resources Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Nodes
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={16}
                          value={hpcResources.nodes}
                          onChange={(e) => setHpcResources({ ...hpcResources, nodes: parseInt(e.target.value) })}
                          className="w-full px-2 py-1.5 text-sm border border-border rounded focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          GPUs
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={8}
                          value={hpcResources.gpus}
                          onChange={(e) => setHpcResources({ ...hpcResources, gpus: parseInt(e.target.value) })}
                          className="w-full px-2 py-1.5 text-sm border border-border rounded focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Memory (GB)
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={512}
                          value={hpcResources.memory_gb}
                          onChange={(e) => setHpcResources({ ...hpcResources, memory_gb: parseInt(e.target.value) })}
                          className="w-full px-2 py-1.5 text-sm border border-border rounded focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Walltime (hours)
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={168}
                          value={hpcResources.walltime_hours}
                          onChange={(e) => setHpcResources({ ...hpcResources, walltime_hours: parseInt(e.target.value) })}
                          className="w-full px-2 py-1.5 text-sm border border-border rounded focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>

                    {/* Checkpointing */}
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="team_checkpoint_enabled"
                        checked={hpcTraining.checkpoint_enabled}
                        onChange={(e) => setHpcTraining({ ...hpcTraining, checkpoint_enabled: e.target.checked })}
                        className="rounded border-border text-primary-600 focus:ring-primary-500"
                      />
                      <label htmlFor="team_checkpoint_enabled" className="text-sm text-foreground">
                        Enable checkpointing (every {hpcTraining.checkpoint_interval_minutes} min)
                      </label>
                    </div>
                  </div>
                )}

                <Button
                  variant="primary"
                  onClick={handleExecute}
                  disabled={teamExecutionStore.isLoading || (executionMode === 'hpc' && !selectedCluster)}
                  className="w-full"
                >
                  {executionMode === 'hpc' ? (
                    <>
                      <ComputerDesktopIcon className="w-4 h-4 mr-2" />
                      Submit to HPC
                    </>
                  ) : (
                    <>
                      <PlayIcon className="w-4 h-4 mr-2" />
                      Execute Team
                    </>
                  )}
                </Button>
              </div>
            </Card>
          )}

          {/* Execution Progress */}
          {execution && (
            <Card>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold text-foreground">Execution Progress</h2>
                    {isExecuting && currentExecutionId && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-blue-500/10 rounded-full">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        <span className="text-xs font-medium text-blue-600">Live</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusIcon(execution.status)}
                    <Badge variant={getStatusBadgeVariant(execution.status)}>
                      {execution.status}
                    </Badge>
                  </div>
                </div>

                {/* Real-time Latest Update Display */}
                {latestUpdate && isExecuting && (
                  <div className="p-3 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <SparklesIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5 animate-pulse" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-blue-600">
                          {latestUpdate.type === 'team_execution.start' && `Starting ${latestUpdate.team_name}...`}
                          {latestUpdate.type === 'team_execution.agent_start' && `${latestUpdate.agent_name} (${latestUpdate.role}) is working...`}
                          {latestUpdate.type === 'team_execution.agent_complete' && `${latestUpdate.agent_name} completed!`}
                          {latestUpdate.type === 'team_execution.progress' && latestUpdate.progress_message}
                        </p>
                        {latestUpdate.current_iteration !== undefined && (
                          <p className="text-xs text-blue-600 mt-1">
                            Iteration {latestUpdate.current_iteration}
                            {latestUpdate.agent_name && ` • ${latestUpdate.agent_name}`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Iteration</p>
                    <p className="text-lg font-semibold text-foreground">
                      {execution.iterations_completed || 0}
                      {team.feedback_enabled && team.max_iterations && (
                        <span className="text-sm text-muted-foreground font-normal">
                          {' '}
                          / {team.max_iterations}
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Tokens Used</p>
                    <p className="text-lg font-semibold text-foreground">
                      {execution.total_tokens?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Cost</p>
                    <p className="text-lg font-semibold text-foreground">
                      ${execution.total_cost?.toFixed(4) || '0.0000'}
                    </p>
                  </div>
                </div>

                {isExecuting && (
                  <Button variant="secondary" onClick={handleCancel} className="w-full">
                    Cancel Execution
                  </Button>
                )}

                {execution.output_data && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">Final Result</h3>
                    <pre className="bg-muted/50 border border-border rounded-lg p-4 text-sm overflow-x-auto">
                      {typeof execution.output_data === 'string'
                        ? execution.output_data
                        : JSON.stringify(execution.output_data, null, 2)}
                    </pre>
                  </div>
                )}

                {execution.error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-red-600 mb-1">Error</h3>
                        <p className="text-sm text-red-600">{execution.error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {execution.created_at && (
                  <div className="text-xs text-muted-foreground">
                    Started {formatDistanceToNow(new Date(execution.created_at), { addSuffix: true })}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Messages */}
          {execution && messages.length > 0 && (
            <Card>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-muted-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">
                    Agent Messages ({messages.length})
                  </h2>
                </div>

                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {Object.entries(teamExecutionStore.messagesByIteration).map(([iteration, iterationMessages]) => (
                    <div key={iteration} className="space-y-2">
                      <div className="flex items-center gap-2 py-2">
                        <ArrowPathIcon className="w-4 h-4 text-primary-600" />
                        <span className="text-sm font-semibold text-primary-700">
                          Iteration {iteration}
                        </span>
                        <div className="flex-1 border-t border-border"></div>
                      </div>

                      {iterationMessages.map((message: any) => (
                        <div
                          key={message.id}
                          className="p-3 bg-muted/50 border border-border rounded-lg"
                        >
                          <div className="flex items-start gap-3">
                            <UserIcon className="w-4 h-4 text-muted-foreground mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-foreground">
                                  {getAgentName(message.sender_agent_id)}
                                </span>
                                {message.receiver_agent_id && (
                                  <>
                                    <span className="text-xs text-muted-foreground">→</span>
                                    <span className="text-sm text-muted-foreground">
                                      {getAgentName(message.receiver_agent_id)}
                                    </span>
                                  </>
                                )}
                                <Badge variant="gray" size="sm">
                                  {message.message_type}
                                </Badge>
                              </div>
                              <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                                {message.content}
                              </p>
                              {message.metadata && Object.keys(message.metadata).length > 0 && (
                                <details className="mt-2">
                                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                                    View metadata
                                  </summary>
                                  <pre className="text-xs text-muted-foreground mt-1 bg-card p-2 rounded border border-border overflow-x-auto">
                                    {JSON.stringify(message.metadata, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                  {/* Auto-scroll anchor */}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Team Info */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Team Info</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Strategy</p>
                <p className="text-sm font-medium text-foreground capitalize">
                  {team.strategy.replace('_', ' ').toLowerCase()}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Members</p>
                <p className="text-sm font-medium text-foreground">{team.members.length}</p>
              </div>
              {team.feedback_enabled && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowPathIcon className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-semibold text-blue-600">Feedback Loop</span>
                  </div>
                  <p className="text-xs text-blue-600">
                    Max {team.max_iterations} iterations
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Team Members */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Team Members</h3>
            <div className="space-y-2">
              {team.members
                .sort((a, b) => a.execution_order - b.execution_order)
                .map((member, index) => (
                  <div
                    key={member.id || index}
                    className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
                  >
                    <span className="text-xs font-mono text-muted-foreground">{index + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {getAgentName(member.agent_id)}
                      </p>
                      <Badge variant={getRoleBadgeVariant(member.role)} size="sm">
                        {member.role}
                      </Badge>
                    </div>
                  </div>
                ))}
            </div>
          </Card>

          {/* Info Box */}
          <Card className="bg-blue-500/10 border-blue-500/20">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-blue-600 mb-1">How it works</h3>
                <p className="text-xs text-blue-600">
                  Team members will execute according to the selected coordination strategy.
                  {team.feedback_enabled &&
                    ' With feedback loop enabled, the team will iterate until validation passes or max iterations reached.'}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
})

export default TeamExecutionPage
